import { CompanyService } from '$/services';
import React, { useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';
import * as Bixby from '$/core';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { AgGridReact } from 'ag-grid-react';
import BixToggleCellRenderer from '$/components/global/bixToggleCellRenderer';
import { ICellRendererParams } from 'ag-grid-enterprise';
import BixButton from '$/components/global/bixButton';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { connect } from 'react-redux';
import dayjs from 'dayjs';

interface ICompanyAccessDetailProps {
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
}

const CompanyAccessDetail: React.FC<ICompanyAccessDetailProps> = ({ dispatchToastError, dispatchToastSuccess }) => {
  const [users, setUsers] = useState([] as Bixby.Models.ComplianceUserCompany[]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const match = useMatch('/compliance/:companyId');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!match?.params.companyId) return;
        setIsLoading(true);
        const response = await CompanyService.getUsersByCompany(match.params.companyId);
        setUsers(response);
      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [match]);

  const saveUserAccess = async () => {
    try {
      if (!match?.params.companyId) return;
      setIsSaving(true);
      await CompanyService.updateUsersByCompany(match.params.companyId, users);
      dispatchToastSuccess('Saved user access');
    } catch (error) {
      dispatchToastError('Failed to save user access');
    } finally {
      setIsSaving(false);
    }
  };
  const usersWithAccess = useMemo(() => {
    return users.filter(_ => _.active);
  }, [users]);

  return isLoading ? (
    <Loading pageLevel />
  ) : hasError ? (
    <p className="error">An error occurred. Please try again later.</p>
  ) : users.length > 0 ? (
    <section className="compliance">
      <h2>Modify Company Access for {users[0].company_Name}</h2>
      <div className="access_detail_contain">
        <div className="save_user_access">
          <p>
            By clicking the "Save User Access" below, you acknowledge and agree that you have independently verified
            that the selected users have access to {users?.[0]?.company_Name}.
          </p>
          <BixButton type="button" onClick={saveUserAccess} useLoading isLoading={isSaving} disabled={isSaving}>
            Save User Access
          </BixButton>
        </div>
        <div className="user_access_grid">
          <h3>Users Access List</h3>
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                enableRangeSelection={true}
                defaultColDef={{
                  flex: 1,
                  minWidth: 100,
                  sortable: true,
                  resizable: true,
                  lockPinned: true,
                  cellClass: 'align-left',
                }}
                gridOptions={{
                  getRowClass: params => (params.data?.['isActive'] ? '' : 'inactive'),
                }}
                rowData={users}
                suppressDragLeaveHidesColumns={true}
                suppressMovableColumns={true}
                animateRows={true}
                rowHeight={36}
                headerHeight={36}
                getRowId={({ data }) => data.user_Id}
                components={{
                  bixToggleCellRenderer: BixToggleCellRenderer,
                }}
                columnDefs={[
                  {
                    maxWidth: 300,
                    headerName: 'Name',
                    field: 'user_FirstName',
                    cellRenderer: (params: ICellRendererParams) => {
                      return `${params.data['user_FirstName']} ${params.data['user_LastName']}`;
                    },
                  },
                  {
                    headerName: 'Email',
                    field: 'user_Email',
                  },
                  {
                    maxWidth: 150,
                    headerName: 'Private Access',
                    field: 'active',
                    cellRenderer: 'bixToggleCellRenderer',
                    cellRendererParams: {
                      checkedValue: 'active',
                      idValue: 'user_Id',
                      getComponentId: (params: ICellRendererParams) => {
                        return `${params.data['user_Id']}.active`;
                      },
                      handleToggle: (user_Id: string, newCheckedState: boolean) => {
                        const user = users.find(_ => _.user_Id === user_Id);
                        if (!user) return;

                        user.active = newCheckedState;
                        setUsers([...users]);
                      },
                      shouldHideToggle: (params: ICellRendererParams) => {
                        return params.data['isClientComplianceAdmin'];
                      },
                    },
                  },
                  {
                    headerName: 'Access Date',
                    field: 'accessDate',
                    cellRenderer: (params: any) => {
                      return params.value ? Bixby.Utils.formatDateForByline(dayjs.utc(params.value)) : 'N/A';
                    },
                  },
                ]}></AgGridReact>
            </div>
          </div>
        </div>
        <div className="users_with_access">
          <h3>Users With Access</h3>
          <dl>
            {usersWithAccess.map((_, ix) => (
              <React.Fragment key={ix}>
                <dt>
                  {_.user_FirstName} {_.user_LastName}
                </dt>
                <dd>{_.user_Email}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </div>
    </section>
  ) : (
    <section className="compliance">
      <NoDataFound dataType="users" />
    </section>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapDispatchToProps)(CompanyAccessDetail);
